// Keep this version stable: a date and an absolute word coordinate identify the
// same terrain on every device. No account ID, clock time or random state is used.
export const TERRAIN_VERSION = 'climbwriter-mountain-v1';
export const PIXELS_PER_WORD = 2.5;

export type Point = { x: number; y: number };
type SurfacePoint = Point & { z: number; groundY: number; cross: number };
export type TerrainFace = { id: string; d: string; light: number; hue: number; saturation: number; opacity: number };

export function daySeed(day: string) {
  return [...`${TERRAIN_VERSION}:${day}`].reduce((seed, char) => Math.imul(seed ^ char.charCodeAt(0), 16777619), 2166136261) >>> 0;
}

function randomAt(seed: number, index: number) {
  let hash = seed ^ Math.imul(index, 374761393);
  hash = Math.imul(hash ^ (hash >>> 16), 2246822519);
  return (hash >>> 0) / 0xffffffff;
}

export function createDailyTerrain(day: string) {
  const seed = daySeed(day);
  const checkpoint = (step: number) => {
    if (step === 0) return 0;
    let hash = seed ^ Math.imul(step, 374761393);
    for (const char of day) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
    hash = Math.imul(hash ^ (hash >>> 16), 2246822519);
    return ((hash >>> 0) / 0xffffffff - .5) * 64;
  };
  const x = (words: number) => {
    const step = Math.floor(words / 50);
    const blend = (1 - Math.cos(Math.PI * (words % 50 / 50))) / 2;
    return checkpoint(step) + (checkpoint(step + 1) - checkpoint(step)) * blend;
  };
  const routePoint = (words: number, offset = 0): Point => {
    const slope = (x(words + .1) - x(Math.max(0, words - .1))) / (words === 0 ? .1 : .2);
    const length = Math.hypot(PIXELS_PER_WORD, slope);
    return {x: x(words) + offset * PIXELS_PER_WORD / length, y: -words * PIXELS_PER_WORD + offset * slope / length};
  };
  // Smooth noise is sampled by world coordinate, never by the visible window.
  const noise = (words: number, scale: number, salt: number) => {
    const index = Math.floor(words / scale), t = words / scale - index;
    const blend = t * t * (3 - 2 * t);
    return randomAt(seed ^ salt, index) * (1 - blend) + randomAt(seed ^ salt, index + 1) * blend;
  };
  const surface = (words: number, side: number, cross: number): SurfacePoint => {
    const edge = routePoint(words, side * 7);
    const width = (side < 0 ? 57 : 76) + 22 * noise(words, 90, side < 0 ? 809 : 1301);
    const ridgeHeight = 66 + 22 * noise(words, 145, 71);
    const spine = Math.pow(1 - cross, 1.65);
    const folds = Math.sin(words / 22 + cross * 7 + noise(words, 110, side * 193) * 5);
    const smallerFolds = Math.sin(words / 9 - cross * 11 + seed % 17);
    const ravine = Math.pow(.5 + .5 * Math.sin(words / 17 + cross * 10 + noise(words, 100, 229) * 4), 3);
    const detail = (folds * 7 + smallerFolds * 1.4 - ravine * 13) * Math.sin(Math.PI * cross);
    const height = ridgeHeight * spine + detail;
    const drop = ridgeHeight - height;
    return {
      x: edge.x + side * width * cross + drop * .13,
      y: edge.y + drop * .8,
      z: height,
      groundY: edge.y,
      cross,
    };
  };
  const face = (a: SurfacePoint, b: SurfacePoint, c: SurfacePoint, id: string): TerrainFace => {
    const ux = b.x - a.x, uy = b.groundY - a.groundY, uz = b.z - a.z;
    const vx = c.x - a.x, vy = c.groundY - a.groundY, vz = c.z - a.z;
    let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    if (nz < 0) { nx = -nx; ny = -ny; nz = -nz; }
    const length = Math.hypot(nx, ny, nz) || 1;
    const illumination = Math.max(0, (nx * -.5 + ny * -.3 + nz * .81) / length);
    const altitude = Math.max(0, -(a.groundY + b.groundY + c.groundY) / (3 * PIXELS_PER_WORD));
    const snow = Math.min(1, Math.max(0, (altitude - 1800) / 6200));
    // Broad patches of stone and vegetation, continuous across section boundaries.
    const vegetation = noise(altitude, 140, 419);
    const cross = (a.cross + b.cross + c.cross) / 3;
    const greenery = Math.sin(Math.PI * Math.min(1, cross * 1.2)) * (1 - snow * .85);
    const light = Math.round(79 + illumination * 10 - greenery * (9 + vegetation * 5) + snow * 7);
    const hue = Math.round(80 + greenery * 26);
    const saturation = Math.round(3 + greenery * (12 + vegetation * 6));
    const point = (p: Point) => `${p.x.toFixed(2)} ${p.y.toFixed(2)}`;
    const edgeBlend = (p: SurfacePoint) => {
      const t = Math.max(0, Math.min(1, (p.cross - .6) / .4));
      const base = Math.min(1, Math.max(0, -p.groundY / 45));
      return (1 - t * t * (3 - 2 * t)) * base;
    };
    const opacity = (edgeBlend(a) + edgeBlend(b) + edgeBlend(c)) / 3;
    return {id, d: `M ${point(a)} L ${point(b)} L ${point(c)} Z`, light, hue, saturation, opacity};
  };
  const section = (start: number) => {
    const first = Math.max(0, Math.floor(start / 20) * 20);
    const faces: TerrainFace[] = [];
    // Fixed mesh size regardless of whether the writer has 10 or 100,000 words.
    for (const side of [-1, 1]) {
      const rows = Array.from({length: 46}, (_, row) => Array.from({length: 8}, (_, col) => surface(first + row * 20, side, col / 7)));
      for (let row = 0; row < 45; row++) for (let col = 0; col < 7; col++) {
        const a = rows[row][col], b = rows[row + 1][col], c = rows[row + 1][col + 1], d = rows[row][col + 1];
        const key = `${side}:${first + row * 20}:${col}`;
        faces.push(face(a, b, c, `${key}:a`), face(a, c, d, `${key}:b`));
      }
    }
    // Sparse details use fixed world cells, so window boundaries never move them.
    const stones = Array.from({length: 46}, (_, index) => {
      const mark = first + index * 20;
      const side = randomAt(seed, mark + 7051) > .5 ? 1 : -1;
      const point = routePoint(mark, side * (12 + randomAt(seed, mark + 7027) * 6));
      return {...point, id: mark, radius: .6 + randomAt(seed, mark + 7039) * 1.2};
    });
    const trees = Array.from({length: 32}, (_, index) => {
      const cell = Math.floor(first / 30) + index;
      const mark = cell * 30 + randomAt(seed, cell + 8101) * 20;
      const side = randomAt(seed, cell + 8107) > .5 ? 1 : -1;
      const cross = .42 + randomAt(seed, cell + 8111) * .4;
      const point = surface(mark, side, cross);
      const density = Math.max(.08, .78 - mark / 9000);
      const rand = (salt: number) => randomAt(seed ^ (cell * 7919), salt);
      // Three deliberately drawn silhouettes; seeded proportions keep each tree distinct.
      const variants = [
        {crown:'M 0 -28 Q -2 -21 -5 -18 L -2 -18 Q -5 -13 -8 -10 L -4 -11 Q -6 -5 -10 -2 Q -4 1 0 0 Q 5 2 9 -2 Q 5 -6 4 -10 L 7 -9 Q 4 -15 2 -18 L 4 -17 Q 1 -23 0 -28 Z', highlights:'M 0 -27 Q -2 -20 -4 -19 L -1 -19 L -6 -11 L -2 -12 L -7 -3 Q -3 -1 0 -2 Z'},
        {crown:'M -1 -24 C -8 -26 -10 -19 -7 -16 C -14 -15 -13 -7 -7 -6 C -5 -1 2 0 5 -5 C 12 -4 14 -12 8 -15 C 10 -22 4 -27 -1 -24 Z', highlights:'M -2 -23 C -8 -24 -9 -18 -5 -16 C -11 -15 -11 -9 -6 -8 Q -2 -7 0 -12 C -3 -14 2 -20 -2 -23 Z'},
        {crown:'M 1 -30 C -2 -26 -3 -23 -3 -21 L -1 -22 Q -5 -17 -5 -14 L -2 -15 Q -7 -9 -7 -5 Q -3 -2 0 -3 Q 5 -1 7 -5 Q 5 -11 3 -14 L 5 -13 Q 4 -19 1 -22 L 3 -21 Z', highlights:'M 0 -28 L -2 -21 L 0 -22 L -4 -14 L -1 -15 L -5 -6 Q -2 -4 0 -5 Z'},
      ];
      const variant = Math.floor(rand(41) * variants.length);
      const {crown, highlights} = variants[variant];
      return {...point, crown, highlights, variant, widthScale:.8 + rand(37)*.35, id:cell, mark, side, scale:.65 + randomAt(seed, cell + 8117) * .6, visible:randomAt(seed, cell + 8123) < density};
    }).filter(tree => tree.visible && Math.abs(tree.x - x(Math.max(0,-tree.y / PIXELS_PER_WORD))) > 27).sort((a,b) => a.y - b.y);
    // Rare, independent river bends in the valley outside the right slope.
    const rivers = Array.from({length:3}, (_, index) => {
      const cell = Math.floor(first / 600) + index;
      const mark = cell * 600;
      if (randomAt(seed, cell + 9101) > .3) return null;
      const points = Array.from({length:65}, (_, i) => {
        const t = i / 64;
        const word = mark + t * 260;
        const riverX = 35 - 77 * Math.sin(Math.PI * t) + 5 * Math.sin(t * Math.PI * 3);
        return `${i ? 'L' : 'M'} ${riverX.toFixed(2)} ${(-word * PIXELS_PER_WORD).toFixed(2)}`;
      });
      return {id:cell,d:points.join(' ')};
    }).filter((river): river is {id:number;d:string} => river !== null);
    return {faces, stones, trees, rivers};
  };
  return {seed, x, routePoint, section, surface, noise};
}
